"use client";
import { cn } from "cn";
import { AnimatePresence, LazyMotion, domAnimation, m } from "motion/react";
import Image, { type StaticImageData } from "next/image";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./fog-background.module.css";
import Fog1 from "./fog1.png";
import Fog2 from "./fog2.png";
import Fog3 from "./fog3.png";
import Fog4 from "./fog4.png";
import Fog5 from "./fog5.png";

export type FogDensity = "clear" | "light" | "medium" | "max";

export const FogBackground = ({
  density,
  image,
}: {
  density: FogDensity;
  image: StaticImageData;
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) return null;

  return createPortal(
    <LazyMotion features={domAnimation}>
      <m.div className="fixed inset-0 -z-10 h-screen w-screen">
        <Fog density={density} />
        <m.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: isImageLoaded ? 1 : 0 }}
          transition={{ duration: 2, ease: "easeIn" }}
        >
          <Image
            src={image}
            alt=""
            fill
            className="object-cover"
            onLoad={() => setIsImageLoaded(true)}
            onLoadingComplete={() => setIsImageLoaded(true)}
            sizes="(max-width: 768px) 120vw, 120vw"
            priority
            placeholder="blur"
          />
        </m.div>
      </m.div>
    </LazyMotion>,
    document.body,
  );
};

const Fog = ({ density }: { density: FogDensity }) => {
  const fogLayers = [
    { id: "fog1-left", src: Fog1, i: 1, exit: "left" },
    { id: "fog2-right", src: Fog2, i: 2, exit: "right" },
    { id: "fog3-left", src: Fog3, i: 3, exit: "left" },
    { id: "fog4-right", src: Fog4, i: 4, exit: "right" },
    { id: "fog5-left", src: Fog5, i: 5, exit: "left" },
    { id: "fog1-right-2", src: Fog1, i: 10, exit: "right" },
    { id: "fog2-left-2", src: Fog2, i: 9, exit: "left" },
    { id: "fog3-right-2", src: Fog3, i: 8, exit: "right" },
    { id: "fog4-left-2", src: Fog4, i: 7, exit: "left" },
    { id: "fog5-right-2", src: Fog5, i: 6, exit: "right" },
    { id: "fog1-left-3", src: Fog1, i: 11, exit: "left" },
    { id: "fog2-right-3", src: Fog2, i: 12, exit: "right" },
    { id: "fog3-left-3", src: Fog3, i: 13, exit: "left" },
    { id: "fog4-right-3", src: Fog4, i: 14, exit: "right" },
    { id: "fog5-left-3", src: Fog5, i: 15, exit: "left" },
    { id: "fog1-right-4", src: Fog1, i: 20, exit: "right" },
    { id: "fog2-left-4", src: Fog2, i: 19, exit: "left" },
    { id: "fog3-right-4", src: Fog3, i: 18, exit: "right" },
    { id: "fog4-left-4", src: Fog4, i: 17, exit: "left" },
    { id: "fog5-right-4", src: Fog5, i: 16, exit: "right" },
  ];

  const visibleLayers = {
    max: fogLayers,
    medium: fogLayers.slice(0, fogLayers.length / 2),
    light: fogLayers.slice(0, fogLayers.length / 3),
    clear: [],
  }[density];

  return (
    <m.div
      className={cn(styles.fogContainer, "relative")}
      transition={{
        duration: ["clear"].includes(density) ? 0.3 : 1,
      }}
    >
      <AnimatePresence mode="sync" initial={false}>
        {visibleLayers.map(({ id, src, i, exit }) => (
          <m.div
            key={id}
            initial={{
              x: exit === "left" ? "100%" : "-100%",
              opacity: 0,
            }}
            animate={{
              x: 0,
              opacity: 1,
            }}
            exit={{
              x: exit === "left" ? "-100%" : "100%",
              opacity: 0,
            }}
            transition={{
              duration: 3,
            }}
            className="absolute inset-0"
          >
            <Image
              src={src}
              alt=""
              className={styles.fogLayer}
              style={{ "--i": i } as React.CSSProperties}
              sizes="(max-width: 768px) 10vw, 10vw"
              quality={1}
            />
          </m.div>
        ))}
      </AnimatePresence>
    </m.div>
  );
};
